const STILL_FRAMES = 10;
const MAX_WAIT_MS = 4000;
const MAX_SCROLLS = 3;

const scrollParent = (element: Element): Element | null => {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    if (/auto|scroll/.test(getComputedStyle(parent).overflowY) && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }
  }
  return null;
};

// In sight within the area that scrolls it (a finder's list is shorter than the window) as well as the window
const isInView = (element: Element) => {
  const { top, bottom } = element.getBoundingClientRect();
  const area = scrollParent(element)?.getBoundingClientRect() ?? { top: 0, bottom: window.innerHeight };
  return top >= Math.max(area.top, 0) && bottom <= Math.min(area.bottom, window.innerHeight);
};

/**
 * Scrolls the element to the middle once it is there and has stopped moving - the branches above it unfolding, the
 * tree being drawn. When it is still out of sight after that, because the tree went on changing under the scroll, it
 * scrolls again (a few times, within 4 seconds). Returns a function that cancels it.
 */
export const scrollIntoViewWhenSettled = (selector: string): (() => void) => {
  const started = performance.now();
  let frame = 0;
  let lastTop: number | null = null;
  let stillFrames = 0;
  let scrolls = 0;
  const step = () => {
    const element = document.querySelector(selector);
    const top = element ? element.getBoundingClientRect().top : null;
    stillFrames = element && top === lastTop ? stillFrames + 1 : 0;
    lastTop = top;
    const timedOut = performance.now() - started > MAX_WAIT_MS;
    if (element && (stillFrames >= STILL_FRAMES || timedOut)) {
      if (isInView(element)) return;
      element.scrollIntoView({ block: 'center', behavior: scrolls ? 'auto' : 'smooth' });
      scrolls += 1;
      stillFrames = 0;
      if (scrolls >= MAX_SCROLLS) return;
    }
    if (timedOut) return;
    frame = requestAnimationFrame(step);
  };
  frame = requestAnimationFrame(step);
  return () => cancelAnimationFrame(frame);
};
